'use client';

import { useEffect, useRef } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import Dialog from '@/components/Dialog';
import { useJump } from '@/components/MagicJump';
import { updateConsentsAfterAuth } from '@/hooks/useComplianceConsent';
import { useRouteCnName } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { COMPLIANCE_SCENE_CODES } from '@/utils/constants';
import { sensorsTrack } from '@/utils/sensors';

type ComplianceScene = {
  texts?: Array<{ content?: string }>;
};

export interface SubscribeConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  content?: ReactNode;
  agreeText?: string;
  cancelText?: string;
}

const SubscribeConsentDialog = ({
  open,
  onOpenChange,
  title = 'Notice',
  agreeText = 'Agree',
  cancelText = 'Cancel',
}: SubscribeConsentDialogProps) => {
  const page = useRouteCnName();
  const previousOpenRef = useRef(open);
  const userEmail = useStore((s) => s.userInfo.email);
  const compliancePolicies = useStore((s) => s.compliancePolicies);
  const scene: ComplianceScene = compliancePolicies?.[COMPLIANCE_SCENE_CODES.EMAIL_SUB];
  const emailSubAgreementHtml = scene?.texts?.[0]?.content || '';
  const toJumpTarget = useJump();

  const closeDialog = () => {
    onOpenChange(false);
  };

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      sensorsTrack('EDMAgreePopUp_Exp', {
        page_name: page || '',
      });
    }
    previousOpenRef.current = open;
  }, [open, page]);

  const trackClick = (btnName: 'Cancel' | 'Agree') => {
    sensorsTrack('EDMAgreePopUp_Click', {
      page_name: page || '',
      btn_name: btnName,
    });
  };

  const handleCancel = () => {
    trackClick('Cancel');
    closeDialog();
  };

  const handleAgree = async () => {
    trackClick('Agree');
    try {
      await updateConsentsAfterAuth({
        sceneCode: COMPLIANCE_SCENE_CODES.EMAIL_SUB,
        extra: { email: userEmail },
        page,
      });
    } finally {
      closeDialog();
    }
  };

  const handleHtmlClick = (e: MouseEvent<HTMLParagraphElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');

    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href) {
        // Handle internal links
        if (href.startsWith('/') || (!href.startsWith('http') && !href.startsWith('mailto:'))) {
          e.preventDefault();
          toJumpTarget({
            jump_type: 3,
            Jump_target: href,
          });
        } else if (href.includes(window.location.origin)) {
          e.preventDefault();
          toJumpTarget({
            jump_type: 3,
            Jump_target: href.replace(window.location.origin, ''),
          });
        } else if (window.location.origin.includes('http://127.0.0.1')) {
          e.preventDefault();
          toJumpTarget({
            jump_type: 3,
            Jump_target: href.replace('https://test-zeelool.xmslol.cn', ''),
          });
        }
      }
    }
  };

  return (
    <Dialog
      open={open}
      title={title}
      okText={agreeText}
      cancelText={cancelText}
      onOk={handleAgree}
      onCancel={handleCancel}
      onClose={handleCancel}
      showCloseIcon={false}
      maxWidth="xs"
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: 'calc(100% - 53px)' },
        },
      }}
    >
      <p
        className="font-br text-14 leading-[22px] text-primary-black [&_a]:text-primary-black [&_a]:underline"
        // 合规 HTML 由后端/global init 下发，与 email-subscribe 页面保持一致
        dangerouslySetInnerHTML={{ __html: emailSubAgreementHtml }}
        onClick={handleHtmlClick}
      />
    </Dialog>
  );
};

export default SubscribeConsentDialog;
